import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const words = ["The", "page", "you're", "looking", "for", "has", "slipped", "through", "the", "cracks."];


  return (
    <>
      <SEO
        title="Page not found (404) | Sorvaine"
        description="The page you're looking for doesn't exist. Return to Sorvaine to manage bids, tenders, and proposals."
        canonical="https://sorvaine.com/404"
        noIndex
      />
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center">
      {/* Geometric grid background */}
      <div className="geometric-grid" />
      
      {/* Ambient gradient orbs */}
      <div className="ambient-orb ambient-orb-primary w-[600px] h-[600px] top-[-200px] right-[-100px]" />
      <div className="ambient-orb ambient-orb-secondary w-[500px] h-[500px] bottom-[-150px] left-[-100px]" />
      <div className="ambient-orb w-[400px] h-[400px] top-[30%] left-[20%] opacity-20" 
           style={{ background: 'radial-gradient(circle, hsl(var(--accent) / 0.3) 0%, transparent 70%)' }} />
      
      {/* Noise overlay */}
      <div className="noise-overlay" />

      <div className="relative z-10 text-center px-6 max-w-2xl mx-auto">
        {/* 404 with shimmer effect */}
        <motion.h1
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-[10rem] md:text-[14rem] font-serif font-bold leading-none tracking-tighter text-shimmer mb-4"
        >
          404
        </motion.h1>

        {/* Word-by-word animated subtitle */}
        <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 mb-10">
          {words.map((word, index) => (
            <motion.span
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.3 + index * 0.08,
                ease: "easeOut",
              }}
              className="text-lg md:text-xl text-muted-foreground"
            >
              {word}
            </motion.span>
          ))}
        </div>

        {/* Premium CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.2 }}
          className="mb-12"
        >
          <Button 
            asChild
            size="lg"
            className="group bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-medium rounded-xl transition-all duration-300"
          >
            <Link to="/">
              Return to Home
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </motion.div>




        {/* Minimal footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 2 }}
          className="mt-16 text-xs text-muted-foreground/50"
        >
          © {new Date().getFullYear()} Sorvaine. All rights reserved.
        </motion.p>
      </div>
    </div>
    </>
  );
};

export default NotFound;
